import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-hot-toast';
import {
  ArrowLeftIcon,
  PencilIcon,
  PlayIcon,
  PauseIcon,
  StopIcon,
  ChartBarIcon,
  UserGroupIcon,
  EyeIcon
} from '@heroicons/react/24/outline';
import { Campaign } from '../../types';
import { useAuthStore } from '../../store/authStore';
import { graphqlApi } from '../../services/graphql';
import { Spinner } from '../common/Spinner';
import { useLocaleFormatters } from '../../utils/localeFormatters';
import { usePermissions } from '../../hooks/usePermissions';
import { CampaignEditForm } from './CampaignEditForm';
import ChannelIcon from './ChannelIcon';
import { normalizeExternalHref } from '../../utils/campaignUtils';

export const CampaignDetailPage: React.FC = () => {
  const { campaignId } = useParams<{ campaignId: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation('campaigns');
  const { t: tc } = useTranslation('common');
  const { user } = useAuthStore();
  const { hasPermission } = usePermissions();
  const { formatDate, formatCurrency, formatNumber } = useLocaleFormatters();

  const [campaign, setCampaign] = useState<Campaign | null>(null);
  const [loading, setLoading] = useState(true);
  const [isEditMode, setIsEditMode] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [applicantCount, setApplicantCount] = useState(0);
  const [matchedCount, setMatchedCount] = useState(0);

  useEffect(() => {
    if (campaignId) {
      fetchCampaignDetails();
      fetchApplicantCount();
    }
  }, [campaignId]);

  const fetchApplicantCount = async () => {
    if (!campaignId) return;
    try {
      const res = await graphqlApi.getMatches({ campaignId } as any);
      if (res.success && res.data?.matches) {
        const activeMatches = res.data.matches;
        setApplicantCount(activeMatches.length);
        setMatchedCount(activeMatches.filter((m: any) => ['accepted', 'completed'].includes(m.status)).length);
      }
    } catch { /* ignore */ }
  };

  const fetchCampaignDetails = async () => {
    if (!campaignId) return;

    setLoading(true);
    try {
      const response = await graphqlApi.getCampaign(campaignId);
      
      if (response.success && response.data) {
        setCampaign(response.data);
      } else {
        toast.error(t('messages.loadingError'));
        navigate('/campaigns');
      }
    } catch (error: any) {
      console.error('Error fetching campaign:', error);
      toast.error(error.message || t('messages.loadingError'));
      navigate('/campaigns');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (newStatus: Campaign['status']) => {
    if (!campaign || updating) return;

    // Block pausing an active campaign while it still has applicants.
    // Switching to `paused` while applications are in flight would strand
    // influencers mid-review, so we gate it here (and also in the UI).
    if (newStatus === 'paused' && applicantCount > 0) {
      toast.error(
        t('messages.cannotPauseWithApplicants', {
          defaultValue: '신청자가 있는 캠페인은 비활성화할 수 없습니다',
        }),
      );
      return;
    }

    setUpdating(true);
    try {
      const updateData: any = {
        status: newStatus,
      };

      const response = await graphqlApi.updateCampaign(campaign.campaignId, updateData);

      if (response.success) {
        setCampaign({ ...campaign, status: newStatus });
        toast.success(t('messages.statusUpdated'));
      } else {
        toast.error(response.message || t('messages.statusUpdateFailed'));
      }
    } catch (error: any) {
      console.error('Error updating campaign status:', error);
      toast.error(error.message || t('messages.statusUpdateFailed'));
    } finally {
      setUpdating(false);
    }
  };

  const handleEditComplete = async (updatedCampaign: Campaign) => {
    // Update local state immediately for instant feedback
    setCampaign(updatedCampaign);
    setIsEditMode(false);
    toast.success(t('messages.updateSuccess'));
    
    // Refresh campaign data from server to ensure we have the latest data
    await fetchCampaignDetails();
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <Spinner />
      </div>
    );
  }

  if (!campaign) {
    return (
      <div className="max-w-5xl mx-auto px-4 pb-16">
        <div className="text-center py-12">
          <p className="text-gray-600">{t('messages.campaignNotFound')}</p>
          <Link to="/campaigns" className="text-blue-600 hover:text-blue-800 mt-4 inline-block">
            {t('list.backToCampaigns')}
          </Link>
        </div>
      </div>
    );
  }

  const isOwner = user?.userId === campaign.userId || user?.userId === campaign.createdBy;
  const canUpdate = hasPermission('campaigns:update') && isOwner;

  // If in edit mode, show the edit form
  if (isEditMode && canUpdate) {
    return (
      <div className="max-w-5xl mx-auto px-4 pb-16">
        <CampaignEditForm
          campaign={campaign}
          onComplete={handleEditComplete}
          onCancel={() => setIsEditMode(false)}
        />
      </div>
    );
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-50 text-green-700';
      case 'draft':
        return 'bg-gray-50 text-gray-700';
      case 'paused':
        return 'bg-yellow-50 text-yellow-700';
      case 'completed':
        return 'bg-blue-50 text-blue-700';
      case 'cancelled':
        return 'bg-red-50 text-red-700';
      default:
        return 'bg-gray-50 text-gray-700';
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 pb-16">
      {/* Breadcrumb */}
      <div className="mb-4">
        <nav className="text-sm text-gray-500 mb-4">
          <button onClick={() => navigate('/campaigns')} className="hover:text-blue-600 transition-colors">{t('list.backToCampaigns')}</button>
          <span className="mx-1.5">&gt;</span>
          <span className="text-gray-900 font-medium">{campaign.campaignName || campaign.title}</span>
        </nav>

        <div className="flex items-start justify-between">
          <div className="flex-1">
            <h1 className="text-2xl font-bold text-gray-900">{campaign.campaignName || campaign.title}</h1>
            <div className="flex items-center flex-wrap gap-x-4 gap-y-2 mt-2">
              <span className={`px-3 py-1 text-sm font-medium rounded-full ${getStatusColor(campaign.status)}`}>
                {campaign.status?.charAt(0).toUpperCase() + campaign.status?.slice(1)}
              </span>
              <span className="text-gray-600 text-sm">
                {t('detail.postedOn')} {formatDate(campaign.createdAt || new Date().toISOString())}
              </span>
            </div>
          </div>
        </div>

        {canUpdate && campaign.status !== 'completed' && campaign.status !== 'cancelled' && (() => {
          const isActive = campaign.status === 'active';
          const pauseBlocked = isActive && applicantCount > 0;
          return (
            <div
              className={`mt-4 flex items-center justify-between gap-4 rounded-xl border-2 px-4 py-3 shadow-sm transition-colors ${
                isActive
                  ? 'border-green-400 bg-green-50'
                  : 'border-amber-400 bg-amber-50'
              }`}
            >
              <div className="flex items-center gap-3 min-w-0">
                <div
                  className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full ${
                    isActive ? 'bg-green-500' : 'bg-amber-500'
                  }`}
                >
                  {isActive ? (
                    <PlayIcon className="h-5 w-5 text-white" />
                  ) : (
                    <PauseIcon className="h-5 w-5 text-white" />
                  )}
                </div>
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-gray-900">
                    {t('detail.campaignToggle')}
                  </div>
                  <div className="text-xs text-gray-600 mt-0.5">
                    {pauseBlocked
                      ? t('messages.cannotPauseWithApplicants', {
                          defaultValue: '신청자가 있는 캠페인은 비활성화할 수 없습니다',
                        })
                      : isActive
                        ? t('actions.pauseTooltip')
                        : t('actions.activateTooltip')}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-3 flex-shrink-0">
                <span
                  className={`px-2.5 py-1 text-xs font-bold rounded-full ${
                    isActive
                      ? 'bg-green-600 text-white'
                      : 'bg-amber-600 text-white'
                  }`}
                >
                  {isActive
                    ? tc('publicDashboard.statusActive')
                    : tc('publicDashboard.statusPaused')}
                </span>
                <button
                  onClick={() => handleStatusChange(isActive ? 'paused' : 'active')}
                  disabled={updating || pauseBlocked}
                  title={
                    pauseBlocked
                      ? t('messages.cannotPauseWithApplicants', {
                          defaultValue: '신청자가 있는 캠페인은 비활성화할 수 없습니다',
                        })
                      : isActive
                        ? t('actions.pauseTooltip')
                        : t('actions.activateTooltip')
                  }
                  className={`relative inline-flex h-7 w-14 items-center rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed ${
                    isActive
                      ? 'bg-green-500 focus:ring-green-500'
                      : 'bg-gray-300 focus:ring-amber-500'
                  }`}
                  role="switch"
                  aria-checked={isActive}
                >
                  <span
                    className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-md transition-transform duration-200 ${
                      isActive ? 'translate-x-8' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
            </div>
          );
        })()}
      </div>

      {/* Campaign Image */}
      {(campaign.representativeImages?.[0]?.fileUrl || campaign.imageUrl) && (
        <div className="mb-4 overflow-hidden">
          <img
            src={campaign.representativeImages?.[0]?.fileUrl || campaign.imageUrl}
            alt={campaign.campaignName || campaign.title}
            className="w-full h-80 object-cover rounded-xl"
          />
        </div>
      )}

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
        <Link
          to={`/campaigns/${campaign.campaignId}/applications`}
          className="bg-white rounded-xl border border-gray-100 p-5 hover:shadow-md transition-shadow"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">{t('detail.applications')}</p>
              <p className="text-3xl font-bold text-gray-900">
                {applicantCount}
              </p>
            </div>
            <UserGroupIcon className="h-12 w-12 text-blue-600" />
          </div>
        </Link>

        <div className="bg-white rounded-xl border border-gray-100 p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">{t('detail.matches')}</p>
              <p className="text-3xl font-bold text-gray-900">{matchedCount}</p>
            </div>
            <ChartBarIcon className="h-12 w-12 text-green-600" />
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-100 p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">{t('registration.fields.providedValue')}</p>
              <p className="text-2xl font-bold text-gray-900">
                {campaign.providedValueDescription || '-'}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="flex flex-col lg:flex-row gap-8">
        {/* Main Content */}
        <div className="lg:w-2/3 space-y-6">
          {/* Advertiser / Brand Info */}
          {(campaign.advertiserName || campaign.brandName || campaign.contactPersonName) && (
            <div className="bg-white rounded-xl border border-gray-100 p-5 space-y-3">
              <h2 className="text-base font-bold text-gray-900">{t('registration.sections.advertiserInfo')}</h2>
              <div className="space-y-3">
                {campaign.advertiserName && (
                  <div className="flex justify-between py-1.5 border-b border-gray-50">
                    <span className="text-sm text-gray-500">{t('registration.fields.advertiserName')}</span>
                    <span className="text-sm font-medium text-gray-900">{campaign.advertiserName}</span>
                  </div>
                )}
                {campaign.brandName && (
                  <div className="flex justify-between py-1.5 border-b border-gray-50">
                    <span className="text-sm text-gray-500">{t('registration.fields.brandName')}</span>
                    <span className="text-sm font-medium text-gray-900">{campaign.brandName}</span>
                  </div>
                )}
                {campaign.contactPersonName && (
                  <div className="flex justify-between py-1.5">
                    <span className="text-sm text-gray-500">{t('registration.fields.contactPersonName')}</span>
                    <span className="text-sm font-medium text-gray-900">{campaign.contactPersonName}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Campaign Basic Info */}
          <div className="bg-white rounded-xl border border-gray-100 p-5 space-y-3">
            <h2 className="text-base font-bold text-gray-900">{t('registration.sections.campaignBasicInfo')}</h2>
            <div className="space-y-3">
              {campaign.campaignType && (
                <div className="flex justify-between py-1.5 border-b border-gray-50">
                  <span className="text-sm text-gray-500">{t('registration.fields.campaignType')}</span>
                  <span className="text-sm font-medium text-gray-900">{t(`registration.campaignTypes.${campaign.campaignType}`)}</span>
                </div>
              )}
              {campaign.campaignCategory && (
                <div className="flex justify-between py-1.5 border-b border-gray-50">
                  <span className="text-sm text-gray-500">{t('registration.fields.campaignCategory')}</span>
                  <span className="text-sm font-medium text-gray-900">
                    {(() => {
                      const catMap: Record<string, string> = {
                        hotel: t('registration.categories.hotel'),
                        fnb: t('registration.categories.fnb'),
                        tour_activity: t('registration.categories.tourActivity'),
                        spa_wellness: t('registration.categories.spaWellness'),
                        aviation_transport: t('registration.categories.aviationTransport'),
                        accommodation: tc('categories.accommodation'),
                        restaurant: tc('categories.restaurant'),
                        cafe: tc('categories.cafe'),
                        tour: tc('categories.tour'),
                        ticket: tc('categories.ticket'),
                        shopping: tc('categories.shopping'),
                        wellness: tc('categories.wellness'),
                        other: t('registration.categories.other'),
                      };
                      return catMap[campaign.campaignCategory!] || campaign.campaignCategory;
                    })()}
                  </span>
                </div>
              )}
              {campaign.contentTypes && campaign.contentTypes.length > 0 && (
                <div className="py-1.5 border-b border-gray-50">
                  <span className="text-xs font-semibold text-gray-400 block mb-2">{t('registration.fields.contentTypes')}</span>
                  <div className="flex flex-wrap gap-2">
                    {campaign.contentTypes.map((ct: string, idx: number) => (
                      <span key={idx} className="px-2.5 py-1 bg-indigo-50 text-indigo-700 text-xs font-medium rounded-full">{ct}</span>
                    ))}
                  </div>
                </div>
              )}
              {campaign.recruitmentPeriod && (
                <div className="flex justify-between py-1.5 border-b border-gray-50">
                  <span className="text-sm text-gray-500">{t('registration.fields.recruitmentPeriod')}</span>
                  <span className="text-sm font-medium text-gray-900">
                    {formatDate(campaign.recruitmentPeriod.startDate)}
                    {campaign.recruitmentPeriod.endDate ? ` ~ ${formatDate(campaign.recruitmentPeriod.endDate)}` : ''}
                  </span>
                </div>
              )}
              {campaign.numberOfRecruits != null && (
                <div className="flex justify-between py-1.5 border-b border-gray-50">
                  <span className="text-sm text-gray-500">{t('registration.fields.numberOfRecruits')}</span>
                  <span className="text-sm font-medium text-gray-900">{formatNumber(campaign.numberOfRecruits)}</span>
                </div>
              )}
              {campaign.announcementDate && (
                <div className="flex justify-between py-1.5">
                  <span className="text-sm text-gray-500">{t('registration.fields.announcementDate')}</span>
                  <span className="text-sm font-medium text-gray-900">{formatDate(campaign.announcementDate)}</span>
                </div>
              )}
            </div>
          </div>

          {/* Product / Service Info */}
          {(campaign.productName || campaign.introductionPageUrl || campaign.providedValueDescription) && (
            <div className="bg-white rounded-xl border border-gray-100 p-5 space-y-3">
              <h2 className="text-base font-bold text-gray-900">{t('registration.sections.productServiceInfo')}</h2>
              <div className="space-y-3">
                {campaign.productName && (
                  <div className="flex justify-between py-1.5 border-b border-gray-50">
                    <span className="text-sm text-gray-500">{t('registration.fields.productName')}</span>
                    <span className="text-sm font-medium text-gray-900">{campaign.productName}</span>
                  </div>
                )}
                {campaign.introductionPageUrl && (
                  <div className="flex justify-between py-1.5 border-b border-gray-50">
                    <span className="text-sm text-gray-500">{t('registration.fields.introductionPageUrl')}</span>
                    <a href={campaign.introductionPageUrl} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline truncate max-w-xs">
                      {campaign.introductionPageUrl}
                    </a>
                  </div>
                )}
                {campaign.providedValueDescription && (
                  <div className="flex justify-between py-1.5 border-b border-gray-50">
                    <span className="text-sm text-gray-500">{t('registration.fields.providedValue')}</span>
                    <span className="text-sm font-medium text-gray-900">{campaign.providedValueDescription}</span>
                  </div>
                )}
                {campaign.provisionMethod && (
                  <div className="flex justify-between py-1.5">
                    <span className="text-sm text-gray-500">{t('registration.fields.provisionMethod')}</span>
                    <span className="text-sm font-medium text-gray-900">
                      {(() => {
                        const methodMap: Record<string, string> = {
                          on_site: t('registration.provisionMethods.onSite'),
                          pre_reservation: t('registration.provisionMethods.preReservation'),
                          voucher: t('registration.provisionMethods.voucher'),
                          custom: t('registration.provisionMethods.custom'),
                        };
                        return methodMap[campaign.provisionMethod!] || campaign.provisionMethod;
                      })()}
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Product / Service Info (v2) */}
          {(campaign.productName || campaign.productIntroduction || (campaign as any).visitAddress) && (
            <div className="bg-white rounded-xl border border-gray-100 p-5 space-y-3">
              <h2 className="text-base font-bold text-gray-900">{t('registration.v2.sectionProduct')}</h2>
              <div className="space-y-3">
                {campaign.productIntroduction && (
                  <div className="py-1.5 border-b border-gray-50">
                    <span className="text-xs font-semibold text-gray-400 block mb-1">{t('registration.v2.productIntroduction')}</span>
                    <p className="text-gray-700 whitespace-pre-wrap">{campaign.productIntroduction}</p>
                  </div>
                )}
                {campaign.productName && (
                  <div className="flex justify-between py-1.5 border-b border-gray-50">
                    <span className="text-sm text-gray-500">{t('registration.v2.productName')}</span>
                    <span className="text-sm font-medium text-gray-900">{campaign.productName}</span>
                  </div>
                )}
                {campaign.providedValueDescription && (
                  <div className="flex justify-between py-1.5 border-b border-gray-50">
                    <span className="text-sm text-gray-500">{t('registration.v2.productValue')}</span>
                    <span className="text-sm font-medium text-gray-900">{campaign.providedValueDescription}</span>
                  </div>
                )}
                {campaign.provisionMethod && (
                  <div className="flex justify-between py-1.5 border-b border-gray-50">
                    <span className="text-sm text-gray-500">{t('registration.v2.provisionMethod')}</span>
                    <span className="text-sm font-medium text-gray-900">{campaign.provisionMethod}</span>
                  </div>
                )}
                {(campaign as any).visitAddress && (
                  <div className="flex justify-between py-1.5 border-b border-gray-50">
                    <span className="text-sm text-gray-500">{t('registration.v2.visitAddress')}</span>
                    <span className="text-sm font-medium text-gray-900">{(campaign as any).visitAddress}</span>
                  </div>
                )}
                {campaign.additionalNotes && (
                  <div className="py-1.5">
                    <span className="text-xs font-semibold text-gray-400 block mb-1">{t('registration.v2.visitNotes')}</span>
                    <p className="text-gray-700 whitespace-pre-wrap">{campaign.additionalNotes}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Campaign Mission (v2) */}
          {((campaign as any).contentChannel || (campaign as any).rewardAmount || campaign.contentWritingRequirements) && (
            <div className="bg-white rounded-xl border border-gray-100 p-5 space-y-3">
              <h2 className="text-base font-bold text-gray-900">{t('registration.v2.sectionMission')}</h2>
              <div className="space-y-3">
                {(campaign as any).contentChannel && (
                  <div className="flex items-center gap-2 py-1.5 border-b border-gray-50">
                    <span className="text-sm text-gray-500">{t('registration.v2.contentChannel')}</span>
                    <ChannelIcon channel={(campaign as any).contentChannel} className="w-5 h-5" />
                    <span className="text-sm font-medium text-gray-900">{t(`registration.v2.channels.${(campaign as any).contentChannel}`)}</span>
                  </div>
                )}
                {(campaign as any).rewardAmount != null && (campaign as any).rewardAmount > 0 && (
                  <div className="py-1.5 border-b border-gray-50">
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-500">{t('registration.v2.reward')}</span>
                      <span className="text-sm font-medium text-gray-900">${formatNumber((campaign as any).rewardAmount)}</span>
                    </div>
                    <div className="flex justify-between mt-1">
                      <span className="text-gray-500 text-sm">{t('registration.v2.influencerSettlement')}</span>
                      <span className="text-blue-600 text-sm font-medium">${formatNumber(Math.round((campaign as any).rewardAmount * 0.8))}</span>
                    </div>
                  </div>
                )}
                {/* Publishing Conditions V2 — read from publishingConditions, fallback to legacy */}
                {(() => {
                  const pc = (campaign as any).publishingConditions;
                  const igConds = pc?.instagram?.conditions || [];
                  const igCollabId = pc?.instagram?.collaboratorId || (campaign as any).instagramCollaboratorId;
                  const blogConds = pc?.blog?.conditions || [];
                  const blogLink = pc?.blog?.insertLink || (campaign as any).blogInsertLink;
                  const ytConds = pc?.youtube?.conditions || [];
                  const ytLink = pc?.youtube?.insertLink || (campaign as any).youtubeInsertLink;
                  return (
                    <>
                      {igConds.length > 0 && (
                        <div className="py-1.5 border-b border-gray-50">
                          <span className="text-xs font-semibold text-gray-400 block mb-2">{t('registration.v2.igConditions')}</span>
                          <div className="flex flex-wrap gap-2">
                            {igConds.map((c: any, i: number) => (
                              <span key={i} className="px-2.5 py-1 bg-pink-50 text-pink-700 text-xs font-medium rounded-full">{t(`registration.v2.igItems.${c.item}`)} × {c.quantity}</span>
                            ))}
                          </div>
                          {igCollabId && (
                            <div className="mt-2 text-sm text-gray-600">
                              <span className="font-medium">공동작업자 ID:</span> <span className="text-gray-900">{igCollabId}</span>
                            </div>
                          )}
                        </div>
                      )}
                      {blogConds.length > 0 && (
                        <div className="py-1.5 border-b border-gray-50">
                          <span className="text-xs font-semibold text-gray-400 block mb-2">{t('registration.v2.blogConditions')}</span>
                          <div className="flex flex-wrap gap-2">
                            {blogConds.map((c: any, i: number) => (
                              <span key={i} className="px-2.5 py-1 bg-green-50 text-green-700 text-xs font-medium rounded-full">{t(`registration.v2.blogItems.${c.item}`)} × {c.quantity}</span>
                            ))}
                          </div>
                          {blogLink && (
                            <div className="mt-2 text-sm text-gray-600">
                              <span className="font-medium">콘텐츠 내 삽입링크:</span> <a href={normalizeExternalHref(blogLink, 'blog')} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline ml-1">{blogLink}</a>
                            </div>
                          )}
                        </div>
                      )}
                      {ytConds.length > 0 && (
                        <div className="py-1.5 border-b border-gray-50">
                          <span className="text-xs font-semibold text-gray-400 block mb-2">{t('registration.v2.ytConditions')}</span>
                          <div className="flex flex-wrap gap-2">
                            {ytConds.map((c: any, i: number) => (
                              <span key={i} className="px-2.5 py-1 bg-red-50 text-red-700 text-xs font-medium rounded-full">{t(`registration.v2.ytItems.${c.item}`)} × {c.quantity}</span>
                            ))}
                          </div>
                          {ytLink && (
                            <div className="mt-2 text-sm text-gray-600">
                              <span className="font-medium">콘텐츠 내 삽입링크:</span> <a href={normalizeExternalHref(ytLink, 'youtube')} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline ml-1">{ytLink}</a>
                            </div>
                          )}
                        </div>
                      )}
                    </>
                  );
                })()}
                {(campaign as any).contentInsertLink && (
                  <div className="flex justify-between py-1.5 border-b border-gray-50">
                    <span className="text-sm text-gray-500">{t('registration.v2.contentInsertLink')}</span>
                    <a href={normalizeExternalHref((campaign as any).contentInsertLink)} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline truncate max-w-xs">{(campaign as any).contentInsertLink}</a>
                  </div>
                )}
                {((campaign as any).announcementStartDate || (campaign as any).visitStartDate) && (
                  <div className="py-1.5 border-b border-gray-50 space-y-1">
                    <span className="text-xs font-semibold text-gray-400 block mb-1">{t('registration.v2.campaignSchedule')}</span>
                    {(campaign as any).alwaysOpening ? (
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-500">{t('registration.v2.announcementPeriod')}</span>
                        <span className="px-2 py-0.5 bg-emerald-50 text-emerald-600 text-xs font-semibold rounded-full">상시선정</span>
                      </div>
                    ) : (campaign as any).announcementStartDate && (
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-500">{t('registration.v2.announcementPeriod')}</span>
                        <span>{formatDate((campaign as any).announcementStartDate)}{(campaign as any).announcementEndDate ? ` ~ ${formatDate((campaign as any).announcementEndDate)}` : ''}</span>
                      </div>
                    )}
                    {(campaign as any).visitStartDate && (
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-500">{t('registration.v2.visitPeriod')}</span>
                        <span>{formatDate((campaign as any).visitStartDate)}{(campaign as any).visitEndDate ? ` ~ ${formatDate((campaign as any).visitEndDate)}` : ''}</span>
                      </div>
                    )}
                    {(campaign as any).uploadDeadlineDays && (
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-500">{t('registration.v2.uploadPeriod')}</span>
                        <span>{t('registration.v2.afterVisit')} {(campaign as any).uploadDeadlineDays}{t('registration.v2.daysWithin')}</span>
                      </div>
                    )}
                    {(campaign as any).discussSchedule && (
                      <span className="inline-block px-2 py-0.5 bg-yellow-50 text-yellow-700 text-xs font-medium rounded">{t('registration.v2.discussOnProgress')}</span>
                    )}
                  </div>
                )}
                {campaign.contentWritingRequirements && (
                  <div className="py-1.5 border-b border-gray-50">
                    <span className="text-xs font-semibold text-gray-400 block mb-1">{t('registration.v2.contentGuide')}</span>
                    <p className="text-gray-700 whitespace-pre-wrap">{campaign.contentWritingRequirements}</p>
                  </div>
                )}
                {(campaign as any).secondarySubmissions?.length > 0 && (
                  <div className="py-1.5 border-b border-gray-50">
                    <span className="text-xs font-semibold text-gray-400 block mb-2">{t('registration.v2.secondarySubmissions')}</span>
                    <table className="w-full text-sm">
                      <thead><tr className="bg-gray-50">
                        <th className="px-2 py-1 text-left text-gray-600">{t('registration.v2.itemSelect')}</th>
                        <th className="px-2 py-1 text-left text-gray-600">{t('registration.v2.quantityVolume')}</th>
                        <th className="px-2 py-1 text-left text-gray-600">{t('registration.v2.remarks')}</th>
                      </tr></thead>
                      <tbody>
                        {(campaign as any).secondarySubmissions.map((s: any, i: number) => (
                          <tr key={i} className="border-t border-gray-50">
                            <td className="px-2 py-1">{t(`registration.v2.secondaryTypes.${s.type}`)}</td>
                            <td className="px-2 py-1">{s.quantity} {s.type === 'video' ? t('registration.v2.minutes') : t('registration.v2.pieces')}</td>
                            <td className="px-2 py-1">{s.note || '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                {(campaign as any).submissionPath && (
                  <div className="flex justify-between py-1.5 border-b border-gray-50">
                    <span className="text-sm text-gray-500">{t('registration.v2.submissionPath')}</span>
                    <span className="text-sm font-medium text-gray-900">{(campaign as any).submissionPath}</span>
                  </div>
                )}
                {(campaign as any).secondarySubmissionGuide && (
                  <div className="py-1.5">
                    <span className="text-xs font-semibold text-gray-400 block mb-1">{t('registration.v2.secondaryGuide')}</span>
                    <p className="text-gray-700 whitespace-pre-wrap">{(campaign as any).secondarySubmissionGuide}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* 2차 활용 제출 항목 / 제출경로 / 가이드 */}
          {((campaign.secondarySubmissions?.length ?? 0) > 0 || campaign.submissionPath || campaign.secondarySubmissionGuide) && (
            <div className="bg-white rounded-xl border border-gray-100 p-5 space-y-3">
              <h2 className="text-base font-bold text-gray-900">2차 활용 제출</h2>

              {(campaign.secondarySubmissions?.length ?? 0) > 0 && (
                <div className="mb-4">
                  <h3 className="text-sm font-medium text-gray-600 mb-2">2차 활용 제출 항목</h3>
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-sm">
                      <thead>
                        <tr className="bg-gray-50">
                          <th className="px-3 py-2 text-left font-medium text-gray-600">항목</th>
                          <th className="px-3 py-2 text-left font-medium text-gray-600">수량</th>
                          <th className="px-3 py-2 text-left font-medium text-gray-600">비고</th>
                        </tr>
                      </thead>
                      <tbody>
                        {campaign.secondarySubmissions?.map((s: any, idx: number) => (
                          <tr key={idx} className="border-t border-gray-50">
                            <td className="px-3 py-2">{s.type === 'video' ? '영상' : '사진'}</td>
                            <td className="px-3 py-2">{s.quantity} {s.type === 'video' ? '분' : '개'}</td>
                            <td className="px-3 py-2">{s.note || '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {campaign.submissionPath && (
                <div className="mb-4">
                  <h3 className="text-sm font-medium text-gray-600 mb-1">제출경로</h3>
                  <p className="text-gray-700">{campaign.submissionPath}</p>
                </div>
              )}

              {campaign.secondarySubmissionGuide && (
                <div>
                  <h3 className="text-sm font-medium text-gray-600 mb-1">2차 활용 제출 요청 가이드</h3>
                  <p className="text-gray-700 whitespace-pre-wrap">{campaign.secondarySubmissionGuide}</p>
                </div>
              )}
            </div>
          )}

          {/* Campaign Guide */}
          {(campaign.productIntroduction || campaign.contentWritingRequirements || campaign.additionalNotes) && (
            <div className="bg-white rounded-xl border border-gray-100 p-5 space-y-3">
              <h2 className="text-base font-bold text-gray-900">{t('registration.sections.campaignGuide')}</h2>
              <div className="space-y-4">
                {campaign.productIntroduction && (
                  <div>
                    <h3 className="text-sm font-medium text-gray-600 mb-1">{t('registration.fields.productIntroduction')}</h3>
                    <p className="text-gray-700 whitespace-pre-wrap">{campaign.productIntroduction}</p>
                  </div>
                )}
                {campaign.contentWritingRequirements && (
                  <div>
                    <h3 className="text-sm font-medium text-gray-600 mb-1">{t('registration.fields.contentWritingRequirements')}</h3>
                    <p className="text-gray-700 whitespace-pre-wrap">{campaign.contentWritingRequirements}</p>
                  </div>
                )}
                {campaign.additionalNotes && (
                  <div>
                    <h3 className="text-sm font-medium text-gray-600 mb-1">{t('registration.fields.additionalNotes')}</h3>
                    <p className="text-gray-700 whitespace-pre-wrap">{campaign.additionalNotes}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Other Info */}
          {(campaign.recommendedKeywords || campaign.remarks || (campaign.attachments && campaign.attachments.length > 0)) && (
            <div className="bg-white rounded-xl border border-gray-100 p-5 space-y-3">
              <h2 className="text-base font-bold text-gray-900">{t('registration.sections.otherInfo')}</h2>
              <div className="space-y-3">
                {campaign.recommendedKeywords && (
                  <div className="py-1.5 border-b border-gray-50">
                    <span className="text-xs font-semibold text-gray-400 block mb-1">{t('registration.fields.recommendedKeywords')}</span>
                    <div className="flex flex-wrap gap-2">
                      {campaign.recommendedKeywords.split(',').map((kw: string, idx: number) => (
                        <span key={idx} className="px-2.5 py-1 bg-blue-50 text-blue-700 text-xs font-medium rounded-full">{kw.trim()}</span>
                      ))}
                    </div>
                  </div>
                )}
                {campaign.attachments && campaign.attachments.length > 0 && (
                  <div className="py-1.5 border-b border-gray-50">
                    <span className="text-xs font-semibold text-gray-400 block mb-1">{t('registration.fields.attachments')}</span>
                    <div className="space-y-1">
                      {campaign.attachments.map((file: any, idx: number) => (
                        <a key={idx} href={file.fileUrl} target="_blank" rel="noopener noreferrer" className="block text-blue-600 hover:underline text-sm">
                          📎 {file.fileName}
                        </a>
                      ))}
                    </div>
                  </div>
                )}
                {campaign.remarks && (
                  <div className="py-1.5">
                    <span className="text-xs font-semibold text-gray-400 block mb-1">{t('registration.fields.remarks')}</span>
                    <p className="text-gray-700 whitespace-pre-wrap">{campaign.remarks}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Legacy: Description (for old campaigns) */}
          {!campaign.campaignName && campaign.description && (
            <div className="bg-white rounded-xl border border-gray-100 p-5 space-y-3">
              <h2 className="text-base font-bold text-gray-900">{t('detail.description')}</h2>
              <p className="text-gray-600 whitespace-pre-wrap leading-relaxed">{campaign.description}</p>
            </div>
          )}

          {/* Legacy: Requirements (for old campaigns) */}
          {campaign.requirements && (
            <div className="bg-white rounded-xl border border-gray-100 p-5 space-y-3">
              <h2 className="text-base font-bold text-gray-900">{t('requirements')}</h2>
              <div className="space-y-3">
                {campaign.requirements.minFollowers && (
                  <div className="flex items-center justify-between py-1.5 border-b border-gray-50">
                    <span className="text-sm text-gray-500">{t('detail.minFollowers')}</span>
                    <span className="text-sm font-medium text-gray-900">{formatNumber(campaign.requirements.minFollowers)}</span>
                  </div>
                )}
                {campaign.requirements.platforms && campaign.requirements.platforms.length > 0 && (
                  <div className="py-1.5 border-b border-gray-50">
                    <span className="text-xs font-semibold text-gray-400 block mb-2">{t('detail.platforms')}</span>
                    <div className="flex flex-wrap gap-2">
                      {campaign.requirements.platforms.map((platform: string, idx: number) => (
                        <span key={idx} className="px-2.5 py-1 bg-indigo-50 text-indigo-700 text-xs font-medium rounded-full">{platform}</span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="lg:w-1/3 space-y-6">
          {/* Campaign Info */}
          <div className="bg-white rounded-xl border border-gray-100 p-5 space-y-3">
            <h3 className="text-base font-bold text-gray-900">{t('detail.campaignInfo')}</h3>
            <div className="space-y-3">
              <div className="flex justify-between py-1.5 border-b border-gray-50">
                <span className="text-sm text-gray-500">{t('detail.category')}</span>
                <span className="text-sm font-medium text-gray-900">
                  {(() => {
                    const cat = campaign.campaignCategory || campaign.category;
                    if (!cat) return '-';
                    const catMap: Record<string, string> = {
                      hotel: t('registration.categories.hotel'),
                      fnb: t('registration.categories.fnb'),
                      tour_activity: t('registration.categories.tourActivity'),
                      spa_wellness: t('registration.categories.spaWellness'),
                      aviation_transport: t('registration.categories.aviationTransport'),
                      accommodation: tc('categories.accommodation'),
                      restaurant: tc('categories.restaurant'),
                      cafe: tc('categories.cafe'),
                      tour: tc('categories.tour'),
                      ticket: tc('categories.ticket'),
                      shopping: tc('categories.shopping'),
                      wellness: tc('categories.wellness'),
                      other: t('registration.categories.other'),
                    };
                    return catMap[cat] || tc(`categories.${cat}`, cat);
                  })()}
                </span>
              </div>
              {campaign.numberOfRecruits != null && (
                <div className="flex justify-between py-1.5 border-b border-gray-50">
                  <span className="text-sm text-gray-500">{t('registration.fields.numberOfRecruits')}</span>
                  <span className="text-sm font-medium text-gray-900">{formatNumber(campaign.numberOfRecruits)}</span>
                </div>
              )}
              {campaign.brandName && (
                <div className="flex justify-between py-1.5 border-b border-gray-50">
                  <span className="text-sm text-gray-500">{t('registration.fields.brandName')}</span>
                  <span className="text-sm font-medium text-gray-900">{campaign.brandName}</span>
                </div>
              )}
              {campaign.providedValueDescription && (
                <div className="flex justify-between py-1.5 border-b border-gray-50">
                  <span className="text-sm text-gray-500">{t('registration.fields.providedValue')}</span>
                  <span className="text-sm font-medium text-gray-900">{campaign.providedValueDescription}</span>
                </div>
              )}
            </div>
          </div>

          {/* Quick Links */}
        </div>
      </div>
    </div>
  );
};
